import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import {
  type MatchCoursesFormType,
  matchCourseSchema
} from '@/schemas/education/match-courses-schema'
import { useEquivalenceStore } from '@/store/equivalence-store'
import type { StudentRequestedCoursesFormMethods } from '@/types/forms/student-requested-courses-form-methods'
import type { Request } from '@/types/requests/request'

export const useExternalTransferReviewForms = (request?: Request) => {
  const equivalenceStore = useEquivalenceStore.getState()

  const defaultValues = {
    observation: '',
    serie: undefined
  }

  const {
    control,
    getValues,
    reset,
    watch,
    handleSubmit,
    trigger,
    setValue,
    formState: { errors, isValid }
  } = useForm<MatchCoursesFormType>({
    defaultValues,
    resolver: zodResolver(matchCourseSchema),
    mode: 'onSubmit',
    reValidateMode: 'onChange'
  })

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: 'courseEquivalences'
  })

  function clearDesiredCourses() {
    formMethods?.setValue?.('courseEquivalences', [])
    request?.courseEquivalences.forEach((equivalence) => {
      formMethods?.append?.({
        desiredCourse: {
          courseId: '',
          name: '',
          workload: 0,
          id: '',
          period: '',
          serie: ''
        },
        previousCourse: equivalence.previousCourse,
        equivalenceStatus: {
          id: ''
        },
        selectedByAI: false
      })
    })
  }

  async function triggerFormValidations() {
    await trigger('courseEquivalences')
    await trigger('serie')
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (request) {
      reset({
        curriculum: request.desiredEducation.curriculum,
        courseEquivalences: request.courseEquivalences,
        automaticCourseEquivalences: [],
        serie: request.desiredEducation.entrySerie ?? ''
      })

      const storedItem = equivalenceStore.getRecord(
        request?.id,
        request.desiredEducation.id
      )

      if (!storedItem) {
        equivalenceStore.addRecord({
          requestId: request.id,
          courseEquivalences: request.courseEquivalences,
          curriculumId: request.desiredEducation.curriculum
        })
      }
    }
  }, [request, reset])

  const formMethods: StudentRequestedCoursesFormMethods = {
    append,
    remove,
    control,
    getValues,
    reset,
    fields,
    watch,
    handleSubmit,
    trigger,
    setValue,
    errors,
    isValid,
    replace
  }

  return {
    states: {
      formMethods
    },
    actions: {
      clearDesiredCourses,
      handleSubmit,
      triggerFormValidations
    }
  }
}
